/**
 * FloatingNodeLabel — Floating name label positioned above the node card.
 *
 * Renders the node name as a semi-transparent label above the top-left corner
 * of the card. Uses nopan/nodrag classes so it doesn't interfere with
 * ReactFlow dragging or panning.
 *
 * Sprint 10f: Redesign node name area
 * Sprint 3 (Task 9): Added inline editing — hover shows pencil icon,
 * click enters edit mode with auto-focused input, blur saves, Escape cancels.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'

export interface FloatingNodeLabelProps {
  /** The name to display */
  name: string
  /** Fallback text when name is empty */
  fallback?: string
  /** Optional SVG icon to display before the name */
  typeIcon?: React.ReactNode
  /** Callback when the user renames the node. If omitted, editing is disabled. */
  onRename?: (newName: string) => void
  /** Optional background color for the label pill (e.g. from node color selection) */
  bgColor?: string
}

const FloatingNodeLabel = memo(function FloatingNodeLabel({
  name,
  fallback = 'Untitled',
  typeIcon,
  onRename,
  bgColor,
}: FloatingNodeLabelProps) {
  const displayName = name || fallback

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  // When entering edit mode, sync editValue to current name and focus/select
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEditing = useCallback(() => {
    if (!onRename) return
    setEditValue(name)
    setIsEditing(true)
  }, [onRename, name])

  const handleSave = useCallback(() => {
    setIsEditing(false)
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== name && onRename) {
      onRename(trimmed)
    }
  }, [editValue, name, onRename])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setEditValue(name)
        setIsEditing(false)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      }
    },
    [name, handleSave],
  )

  if (isEditing) {
    return (
      <div
        data-testid="floating-node-label"
        className="absolute left-1.5 nopan nodrag nowheel"
        style={{
          bottom: '100%',
          marginBottom: 6,
        }}
      >
        <input
          ref={inputRef}
          data-testid="floating-node-label-input"
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="text-sm font-semibold text-foreground/80 bg-transparent border-b border-foreground/30 focus:border-primary outline-none max-w-[300px] px-0 py-0"
          style={{ minWidth: 60 }}
        />
      </div>
    )
  }

  return (
    <div
      data-testid="floating-node-label"
      className={`absolute left-1.5 nopan nodrag select-none group/label ${onRename ? '' : 'pointer-events-none'}`}
      style={{
        bottom: '100%',
        marginBottom: 6,
      }}
    >
      <div
        className="flex items-center gap-1 max-w-[340px]"
        style={bgColor ? {
          backgroundColor: bgColor,
          borderRadius: 6,
          paddingLeft: 8,
          paddingRight: 8,
          paddingTop: 2,
          paddingBottom: 2,
        } : undefined}
      >
        {typeIcon && (
          <span className="w-3.5 h-3.5 flex-shrink-0 text-foreground/50">
            {typeIcon}
          </span>
        )}
        <span
          className="text-sm font-semibold truncate"
          style={{ color: bgColor ? '#ffffff' : undefined, opacity: bgColor ? 1 : 0.8 }}
        >
          {displayName}
        </span>
        {onRename && (
          <button
            data-testid="floating-node-label-edit-btn"
            onClick={(e) => {
              e.stopPropagation()
              handleStartEditing()
            }}
            className="opacity-0 group-hover/label:opacity-100 transition-opacity w-5 h-5 rounded hover:bg-surface-1 text-foreground/50 hover:text-foreground flex items-center justify-center flex-shrink-0"
            title="Rename"
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
    </div>
  )
})

FloatingNodeLabel.displayName = 'FloatingNodeLabel'
export { FloatingNodeLabel }
